<template>
    <li v-if="$device.small">
        <a
            class="uk-accordion-title uk-padding-small"
            href="#"
        >
            <div class="uk-flex uk-flex-column">
                <div>
                    <span>
                        {{ refundRequest.requested_by }}
                    </span>
                </div>
                <div>
                    ${{ refundRequest.refundable_details.amount }}
                </div>
                <div>
                    <refund-status-badge
                        :value="rowData['Status']"
                        :tooltip="statusToolTip"
                    />
                </div>
            </div>
        </a>
        <div class="uk-accordion-content">
            <div class="uk-flex uk-flex-wrap uk-margin-bottom">
                <div class="uk-width-1-1 uk-margin-small-bottom">
                    <strong>ID</strong>
                    {{ refundRequest.id }}
                </div>
                <div
                    v-if="allRefundsSection"
                    class="uk-width-1-1 uk-margin-small-bottom"
                >
                    <strong>To Be Approved By</strong>
                    {{ refundRequest.to_be_approved_by }}
                </div>
                <div class="uk-width-1-1 uk-margin-small-bottom">
                    <strong v-if="statusIsRejected">Rejected By</strong>
                    <strong v-else>Approved By</strong>
                    {{ refundRequest.approved_by }}
                </div>
                <div class="uk-width-1-1 uk-margin-small-bottom">
                    <strong v-if="statusIsRejected">Rejected Date</strong>
                    <strong v-else>Approved Date</strong>
                    {{ refundRequest.approved_date }}
                </div>
                <div class="uk-width-1-1 uk-margin-small-bottom">
                    <strong>Cancellation Reason</strong>
                    {{ refundRequest.refundable_details.cancellation_reason }}
                </div>
            </div>
            <div
                v-if="requiresManualVerification"
                class="uk-margin-bottom"
            >
                <span uk-icon="warning" /> Please process this refund manually and click "Verify" when complete.
            </div>
            <div class="buttons">
                <button
                    type="button"
                    class="uk-button uk-button-default uk-button-small uk-margin-right"
                    @click="view"
                >
                    View
                </button>
                <span v-if="statusIsSubmitted">
                    <span v-if="showApprovalButtons">
                        <button
                            type="button"
                            class="uk-button uk-button-default uk-button-small uk-margin-right uk-button-primary"
                            @click="approve"
                        >
                            Approve
                        </button>
                        <button
                            type="button"
                            class="uk-button uk-button-default uk-button-small uk-button-danger"
                            @click="reject"
                        >
                            Reject
                        </button>
                    </span>
                </span>
                <span v-if="showCancelButton">
                    <button
                        type="button"
                        class="uk-button uk-button-default uk-button-small uk-margin-right uk-button-primary"
                        @click="cancel"
                    >
                        Cancel
                    </button>
                </span>
                <span v-if="statusIsPending">
                    <span v-if="showVerifyButton">
                        <button
                            type="button"
                            class="uk-button uk-button-default uk-button-small uk-margin-right"
                            @click="verify"
                        >
                            Verify
                        </button>
                    </span>
                </span>
            </div>
        </div>
    </li>
    <div
        v-else
        style="display: contents"
    >
        <tr>
            <td
                v-for="(column, index) in visibleColumns"
                :key="index"
            >
                <span v-if="column === 'Receipt #'">
                    <a
                        class="uk-link uk-link-text uk-text-primary"
                        @click="view"
                    >
                        {{ rowData[column] }}
                    </a>
                </span>
                <span v-else-if="column === 'Status'">
                    <refund-status-badge
                        :value="rowData[column]"
                        :tooltip="statusToolTip"
                    />
                </span>
                <span v-else>
                    {{ rowData[column] }}
                </span>
            </td>
            <td>
                <div class="buttons">
                    <span v-if="statusIsSubmitted">
                        <span v-if="showApprovalButtons">
                            <span
                                class="uk-link uk-link-text uk-margin-right"
                                @click="approve"
                            >
                                Approve
                            </span>
                            <span
                                class="uk-link uk-link-text uk-margin-right"
                                @click="reject"
                            >
                                Reject
                            </span>
                        </span>
                    </span>
                    <span v-if="showCancelButton">
                        <span
                            class="uk-link uk-link-text uk-margin-right"
                            @click="cancel"
                        >
                            Cancel
                        </span>
                    </span>
                    <span v-if="statusIsPending">
                        <span v-if="showVerifyButton">
                            <span
                                class="uk-link uk-link-text uk-margin-right"
                                @click="verify"
                            >
                                Verify
                            </span>
                        </span>
                    </span>
                </div>
            </td>
        </tr>
        <tr v-if="requiresManualVerification">
            <td :colspan="visibleColumns.length + 1">
                <span uk-icon="warning" /> Please process this refund manually and click "Verify" when complete.
            </td>
        </tr>
    </div>
</template>

<script>
import { RefundStatuses, RefundsTableColumns } from "../../helpers/constants";
import { showApproveOrRejectButtons, showCancelButton, showVerifyButton, filterNullThenCombine, getSalesforceAcctName } from "../../helpers/helpers";
import { mapGetters } from "vuex";
import { formatPrice } from "@strangeanimals/common-vue-components/src/helpers/helpers";
const RefundStatusBadge = () => import("./RefundStatusBadge");

export default {
    components: { RefundStatusBadge },
    props: {
        refundRequest: {
            type: Object,
            default: function () {
                return {};
            }
        },
        sectionName: {
            type: String,
            required: true
        },
        visibleColumns: {
            type: Array,
            default: function () {
                return [];
            }
        },
    },
    computed: {
        ...mapGetters({
            user: 'user',
            canUserApproveRefunds: 'canUserApproveRefunds',
            isUserRefundsAdmin: 'isUserRefundsAdmin'
        }),
        jobDetails() {
            let parts = [this.refundRequest.refundable_details.job_number, this.refundRequest.refundable_details.name];
            return filterNullThenCombine(parts, ' - ');
        },
        amount() {
            return formatPrice(this.refundRequest.refundable_details.amount);
        },
        statusIsSubmitted() {
            return this.refundRequest.status === RefundStatuses.SUBMITTED;
        },
        statusIsPending() {
            return this.refundRequest.status === RefundStatuses.PENDING;
        },
        statusIsRejected() {
            return this.refundRequest.status === RefundStatuses.REJECTED;
        },
        statusIsVerified() {
            return this.refundRequest.status === RefundStatuses.VERIFIED;
        },
        forApproval() {
            return this.sectionName === 'approval';
        },
        forVerification() {
            return this.sectionName === 'verification';
        },
        allRefundsSection() {
            return this.sectionName === 'all-refunds';
        },
        showApprovalButtons() {
            return showApproveOrRejectButtons(this.refundRequest) && (this.forApproval || this.allRefundsSection);
        },
        showCancelButton() {
            return showCancelButton(this.refundRequest);
        },
        showVerifyButton() {
            return showVerifyButton(this.refundRequest) && (this.forVerification || this.allRefundsSection);
        },
        statusToolTip() {
            return this.refundRequest.status + " -- " + this.refundRequest.status_description;
        },
        requiresManualVerification() {
            return this.statusIsPending && this.refundRequest.status_description === 'Please process manually.';
        },
        rowData() {
            let result = {};
            result[RefundsTableColumns.JOB] = this.jobDetails;
            result[RefundsTableColumns.SALESFORCE_ACCOUNT] = getSalesforceAcctName(this.refundRequest.salesforce_account_title);
            result[RefundsTableColumns.RECEIPT_NUMBER] = this.refundRequest.refundable_details.salesforce_receipt_number;
            result[RefundsTableColumns.PAYMENT_METHOD] = this.refundRequest.payment_method;
            result[RefundsTableColumns.LAND_ADDRESS] = this.refundRequest.refundable_details.site_address;
            result[RefundsTableColumns.CONSULTANT] = this.refundRequest.refundable_details.consultant;
            result[RefundsTableColumns.MANAGER] = this.refundRequest.refundable_details.manager;
            result[RefundsTableColumns.REQUESTED_BY] = this.refundRequest.requested_by;
            result[RefundsTableColumns.STATUS] = this.requiresManualVerification ? 'Verify' : this.refundRequest.status;
            result[RefundsTableColumns.TO_BE_APPROVED_BY] = this.refundRequest.to_be_approved_by;

            result[RefundsTableColumns.APPROVED_BY] = this.refundRequest.approved_by;
            result[RefundsTableColumns.APPROVED_DATE] = this.refundRequest.approved_date;

            if (this.statusIsRejected) {
                let note = '';
                note = ' (Rejected)';
                result[RefundsTableColumns.APPROVED_BY] += note;
                result[RefundsTableColumns.APPROVED_DATE] += note;
            }

            result[RefundsTableColumns.AMOUNT] = '$' + this.amount;
            result[RefundsTableColumns.CANCELLATION_REASON] = this.refundRequest.refundable_details.cancellation_reason;
            return result;
        },
    },
    methods: {
        view() {
            let link = this.$router.resolve({name: 'RefundRequestDetail', params: {token: this.refundRequest.token}});
            window.open(link.href, '_blank');
        },
        approve() {
            return this.$emit('approve', this.refundRequest.token);
        },
        reject() {
            return this.$emit('reject', this.refundRequest.token);
        },
        verify() {
            return this.$emit('verify', this.refundRequest.token);
        },
        cancel() {
            return this.$emit('cancel', this.refundRequest.token);
        },
    }
}
</script>
